package com.wintermar.inspection.feature.data.repository.answer

import com.apollographql.apollo.ApolloClient
import com.wintermar.inspection.AppDatabase
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.domain.handleAsFlow
import com.wintermar.inspection.core.ui.component.image.util.FileUploadStatus
import com.wintermar.inspection.feature.data.local.db.SqliteCompat
import com.wintermar.inspection.feature.data.remote.mapper.answer.toListAnswerModel
import com.wintermar.inspection.feature.data.remote.mapper.answer.toListAnswerModelFromFailedSubmissions
import com.wintermar.inspection.feature.data.remote.mapper.answer.toListAnswerModelFromUploadStatuses
import com.wintermar.inspection.feature.data.remote.mapper.answer.toUpsertInspectionCommandInput
import com.wintermar.inspection.feature.data.remote.request.HeaderRequest
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.repository.answer.AnswerRepository
import com.wintermar.inspection.graphql.UpsertInspectionMutation
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext

class AnswerRepositoryImpl(
    private val apolloClient: ApolloClient,
    private val appDatabase: AppDatabase
) : AnswerRepository {
    private val _executableInsertQuery by lazy {
        SqliteCompat.selectInsertImplementation(
            appDatabase = appDatabase,
            upsert = appDatabase.answerTableQueries::insertAnswer,
            replace = appDatabase.answerTableQueries::insertReplaceAnswer
        )
    }

    private fun processInsertAnswer(answer: AnswerModel) {
        _executableInsertQuery(
            answer.id,
            answer.inspectionId,
            answer.questionId,
            answer.notes,
            answer.isRequiredFileOrNotes,
            answer.answerDateIso,
            answer.answerOptionId,
            answer.answerText,
            answer.lastUpdatedIsoUtc0,
            answer.isSubmitted,
            answer.findingCategory?.name
        )
    }

    override suspend fun insertAnswer(answer: AnswerModel): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    processInsertAnswer(answer)
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun insertAnswers(answers: List<AnswerModel>): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    answers.forEach { answer ->
                        processInsertAnswer(answer)
                    }
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun getAnswersFromLocalDb(inspectionId: String): Result<List<AnswerModel>> {
        return withContext(Dispatchers.Default) {
            try {
                val result = appDatabase.transactionWithResult {
                    appDatabase.answerTableQueries
                        .getAnswersByInspectionId(inspectionId)
                        .executeAsList()
                        .toListAnswerModel()
                }
                Result.Success(result)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun submitAnswer(
        answer: AnswerModel,
        answerType: InspectionAnswerType
    ): Flow<FlowResult<String?>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .mutation(
                        UpsertInspectionMutation(
                            answer.toUpsertInspectionCommandInput(answerType)
                        )
                    )
                    .addHttpHeader(HeaderRequest.OPERATION, UpsertInspectionMutation.OPERATION_NAME)
                    .execute()
            }
        ) { response ->
            response.data?.upsertInspection?.lastUpdatedAt?.toString()
        }
    }

    override suspend fun getFileByStatuses(statuses: List<FileUploadStatus>): Result<List<AnswerModel>> {
        return withContext(Dispatchers.Default) {
            try {
                val result = appDatabase.transactionWithResult {
                    appDatabase.answerTableQueries
                        .getByUploadStatuses(statuses.map { it.name })
                        .executeAsList()
                        .toListAnswerModelFromUploadStatuses()
                }
                Result.Success(result)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun getFailedSubmit(): Result<List<Pair<AnswerModel, InspectionAnswerType>>> {
        return withContext(Dispatchers.Default) {
            try {
                val result = appDatabase.transactionWithResult {
                    appDatabase.answerTableQueries
                        .getFailedSubmissions()
                        .executeAsList()
                        .toListAnswerModelFromFailedSubmissions()
                }
                Result.Success(result)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun deleteByInspectionId(inspectionId: String): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerTableQueries.deleteAnswersByInspectionId(inspectionId)
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }
}
